/**
 * Auth slice — single source of truth for the current user's identity and role.
 *
 * Hydrated once from localStorage on store creation (localStorage is the persistence
 * layer written by apiClient on login / token refresh). Components read from here
 * via selectors; direct localStorage.getItem calls inside components are avoided.
 */
import { createSlice, createSelector, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./store";

interface AuthState {
  username: string | null;
  name: string | null;
  role: string | null;
  isAdmin: boolean;
  isCustomer: boolean;
  /** Parsed TabPermissions map — null means no RBAC record exists for this user. */
  tabPermissions: Record<string, boolean> | null;
}

/** Reads all auth fields from localStorage. Used at store init and after token refresh. */
export function readAuthFromStorage(): AuthState {
  try {
    const role = localStorage.getItem("Role");
    const tabRaw = localStorage.getItem("TabPermissions");
    return {
      username: localStorage.getItem("Username"),
      name: localStorage.getItem("Name"),
      role,
      isAdmin: localStorage.getItem("IsAdmin") === "true",
      isCustomer: role === "Customer",
      tabPermissions: tabRaw ? (JSON.parse(tabRaw) as Record<string, boolean>) : null,
    };
  } catch {
    return {
      username: null,
      name: null,
      role: null,
      isAdmin: false,
      isCustomer: false,
      tabPermissions: null,
    };
  }
}

const authSlice = createSlice({
  name: "auth",
  initialState: readAuthFromStorage,
  reducers: {
    /** Full replacement — call after login or token refresh to keep Redux in sync. */
    setAuth: (_state, action: PayloadAction<AuthState>) => action.payload,
    /** Wipe auth state on logout / session expiry. */
    clearAuth: (): AuthState => ({
      username: null,
      name: null,
      role: null,
      isAdmin: false,
      isCustomer: false,
      tabPermissions: null,
    }),
  },
});

export const { setAuth, clearAuth } = authSlice.actions;
export default authSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectUsername = (state: RootState) => state.auth.username;
export const selectName = (state: RootState) => state.auth.name;
export const selectRole = (state: RootState) => state.auth.role;
export const selectIsAdmin = (state: RootState) => state.auth.isAdmin;
export const selectIsCustomer = (state: RootState) => state.auth.isCustomer;
export const selectTabPermissions = (state: RootState) => state.auth.tabPermissions;

/**
 * Returns the list of tab codes the user has access to,
 * or null if no RBAC record exists (fall back to role-based logic).
 * Memoized via createSelector so Object.keys() doesn't produce a new reference on every call.
 */
export const selectAllowedTabs = createSelector(
  selectTabPermissions,
  (tabPermissions): string[] | null =>
    tabPermissions !== null ? Object.keys(tabPermissions) : null,
);
